import { type FC, useContext, useEffect, useState } from 'react';

import AddToGroupModal from '@/components/AddToGroupModal';
import EnrolledGroupsBanner from '@/components/EnrolledGroupsBanner';
import TreatmentGroupCard from '@/components/TreatmentGroupCard';
import type { Props } from '@/components/TreatmentGroupPicker/types';
import AppContext from '@/contexts/appContext';
import useAddToGroupFlow from '@/hooks/useAddToGroupFlow';
import useInfiniteScrollSentinel from '@/hooks/useInfiniteScrollSentinel';
import {
  useEligibleTreatmentGroupsForPerson,
  useEnrolledTreatmentGroupsForPerson,
  useIneligibleTreatmentGroupsForPerson
} from '@/hooks/useTreatmentGroupsForPersonInfinite';
import { TreatmentGroupForPerson } from '@/types/treatmentGroup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Input,
  Spinner,
  Typography
} from '@/ui';
import {
  hasPermission,
  TREATMENT_GROUP_MEMBERSHIP_ADD,
  TREATMENT_GROUP_MEMBERSHIP_CHANGE
} from '@/utils/userPermission';

const SEARCH_DEBOUNCE_MS = 300;

const TreatmentGroupPicker: FC<Props> = ({
  appointmentTypeId,
  memberFirstName,
  onSelectGroup,
  personId,
  timeZone
}) => {
  const { permissions } = useContext(AppContext);
  const canEnroll = hasPermission({
    permissions,
    requiredPermission: TREATMENT_GROUP_MEMBERSHIP_ADD
  });
  const canRemove = hasPermission({
    permissions,
    requiredPermission: TREATMENT_GROUP_MEMBERSHIP_CHANGE
  });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [notEligibleOpen, setNotEligibleOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const params = { appointmentTypeId, ...(search && { search }), ...(timeZone && { timeZone }) };

  const enrolled = useEnrolledTreatmentGroupsForPerson(personId, params);
  const eligible = useEligibleTreatmentGroupsForPerson(personId, params);
  const ineligible = useIneligibleTreatmentGroupsForPerson(personId, params, notEligibleOpen);

  const enrolledSentinelRef = useInfiniteScrollSentinel(
    enrolled.hasNextPage,
    enrolled.isFetchingNextPage,
    enrolled.fetchNextPage
  );
  const eligibleSentinelRef = useInfiniteScrollSentinel(
    eligible.hasNextPage,
    eligible.isFetchingNextPage,
    eligible.fetchNextPage
  );
  const ineligibleSentinelRef = useInfiniteScrollSentinel(
    ineligible.hasNextPage,
    ineligible.isFetchingNextPage,
    ineligible.fetchNextPage
  );

  const membershipActions = useAddToGroupFlow(personId, timeZone);

  const handleEnroll = (group: TreatmentGroupForPerson) => membershipActions.startEnroll(group);

  const handleRemove = (group: TreatmentGroupForPerson) =>
    membershipActions.remove({
      membershipId: group.member_eligibility.membership_id,
      treatmentGroupId: group.id,
      confirmMessage: `Remove this member from "${group.name}"?`
    });

  return (
    <div className="space-y-4">
      <Input
        label="Search"
        placeholder="Search groups or counselors…"
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
      />

      {enrolled.isPending || eligible.isPending ? (
        <Spinner label="Loading treatment groups..." />
      ) : enrolled.isError || eligible.isError ? (
        <Alert variant="error" message="Failed to load treatment groups." />
      ) : (
        <>
          {enrolled.items.length > 0 && (
            <EnrolledGroupsBanner
              groups={enrolled.items}
              isFetchingNextPage={enrolled.isFetchingNextPage}
              isPending={membershipActions.isPending}
              memberFirstName={memberFirstName}
              onRemove={canRemove ? handleRemove : undefined}
              onSelect={onSelectGroup}
              sentinelRef={enrolledSentinelRef}
            />
          )}

          <div className="space-y-2">
            {eligible.items.length === 0 ? (
              <Typography variant="body" muted className="text-sm">
                No eligible groups found.
              </Typography>
            ) : (
              eligible.items.map((group: TreatmentGroupForPerson) => (
                <TreatmentGroupCard
                  key={group.id}
                  group={group}
                  isPending={membershipActions.isPending}
                  memberFirstName={memberFirstName}
                  onEnroll={canEnroll ? handleEnroll : undefined}
                  onSelect={onSelectGroup}
                />
              ))
            )}
            <div ref={eligibleSentinelRef} className="flex justify-center py-2">
              {eligible.isFetchingNextPage && <Spinner label="Loading more groups..." />}
            </div>
          </div>

          <Accordion variant="subtle">
            <AccordionItem defaultOpen={false} onOpenChange={setNotEligibleOpen}>
              <AccordionTrigger>Not eligible</AccordionTrigger>
              <AccordionContent>
                {ineligible.isPending ? (
                  <Spinner label="Loading groups..." />
                ) : ineligible.isError ? (
                  <Alert variant="error" message="Failed to load groups." />
                ) : ineligible.items.length === 0 ? (
                  <Typography variant="body" muted className="text-sm">
                    No groups found.
                  </Typography>
                ) : (
                  <div className="space-y-2">
                    {ineligible.items.map((group: TreatmentGroupForPerson) => (
                      <TreatmentGroupCard
                        key={group.id}
                        group={group}
                        isDimmed
                        memberFirstName={memberFirstName}
                        onSelect={onSelectGroup}
                      />
                    ))}
                    <div ref={ineligibleSentinelRef} className="flex justify-center py-2">
                      {ineligible.isFetchingNextPage && <Spinner label="Loading more groups..." />}
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </>
      )}
      {membershipActions.modal && <AddToGroupModal {...membershipActions.modal} />}
    </div>
  );
};

export default TreatmentGroupPicker;
